import React from 'react';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Breadcrumb from 'react-bootstrap/Breadcrumb';

type Primitive = string | number | boolean;
type QueryValue = Primitive | ReadonlyArray<Primitive | null> | null | undefined;
type Query = Record<string, QueryValue>;

/**
 * Representa um destino compatível com a prop `href` de `next/link`.
 */
export type LinkHref = string | {
  pathname?: string;
  query?: Query;
  hash?: string;
};

/**
 * Item individual do breadcrumb.
 */
export type Crumb = {
  label: string;
  href?: LinkHref;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  icon?: React.ReactNode;
  disabled?: boolean;
  'data-testid'?: string;
};

/**
 * Propriedades aceitas pelo componente `AppBreadcrumb`.
 */
export type AppBreadcrumbProps = {
  items: Crumb[];
  variant?: 'mui' | 'bootstrap';
  separator?: React.ReactNode;
  maxItems?: number;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
};

type InternalCrumb = Crumb & { key: string; __ellipsis?: boolean };

type NextLinkComponent = React.ComponentType<{
  href: LinkHref;
  passHref?: boolean;
  legacyBehavior?: boolean;
  children?: React.ReactNode;
}>;

const ELLIPSIS_LABEL = '\u2026';

let nextLinkCache: NextLinkComponent | null | undefined;

const getOptionalNextLink = (): NextLinkComponent | null => {
  if (nextLinkCache !== undefined) {
    return nextLinkCache;
  }

  nextLinkCache = null;
  try {
    // eslint-disable-next-line no-implied-eval
    const dynamicRequire = Function('return typeof require === "function" ? require : null;')() as ((id: string) => unknown) | null;
    if (!dynamicRequire) {
      return nextLinkCache;
    }

    const moduleCandidate = dynamicRequire('next/link') as { default?: NextLinkComponent } | NextLinkComponent;
    nextLinkCache = (moduleCandidate as { default?: NextLinkComponent })?.default ?? (moduleCandidate as NextLinkComponent) ?? null;
    return nextLinkCache;
  } catch (error) {
    nextLinkCache = null;
    return nextLinkCache;
  }
};

const toHrefString = (href: LinkHref): string => {
  if (typeof href === 'string') {
    return href;
  }

  const pathname = href.pathname ?? '';
  const query = href.query;
  const hash = href.hash ? `#${href.hash.replace(/^#/, '')}` : '';

  if (!query) {
    return `${pathname}${hash}`;
  }

  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item === null || item === undefined) {
          return;
        }
        params.append(key, String(item));
      });
      return;
    }

    params.set(key, String(value));
  });

  const queryString = params.toString();
  return `${pathname}${queryString ? `?${queryString}` : ''}${hash}`;
};

interface LinkAdapterProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  href: LinkHref;
  children: React.ReactNode;
}

const LinkAdapter = React.forwardRef<HTMLAnchorElement, LinkAdapterProps>((props, ref) => {
  const { href, children, onClick, ...rest } = props;
  const nextLink = getOptionalNextLink();

  if (nextLink) {
    return React.createElement(
      nextLink,
      { href, passHref: true, legacyBehavior: true },
      React.createElement('a', { ...rest, ref, onClick }, children),
    );
  }

  return (
    <a href={toHrefString(href)} ref={ref} onClick={onClick} {...rest}>
      {children}
    </a>
  );
});

LinkAdapter.displayName = 'LinkAdapter';

const isEllipsis = (crumb: InternalCrumb): boolean => crumb.__ellipsis === true;

const withKeys = (items: Crumb[]): InternalCrumb[] =>
  items.map((item, index) => ({ ...item, key: `crumb-${index}-${item.label}` }));

const compactItems = (items: InternalCrumb[], maxItems?: number): InternalCrumb[] => {
  if (!maxItems || items.length <= maxItems) {
    return items;
  }

  if (maxItems <= 1) {
    return [items[items.length - 1]];
  }

  const first = items[0];
  const last = items[items.length - 1];

  if (maxItems === 2) {
    return [first, last];
  }

  const tailLength = Math.max(1, Math.min(items.length - 1, maxItems - 2));
  const tail = items.slice(-tailLength);

  const ellipsisCrumb: InternalCrumb = {
    label: ELLIPSIS_LABEL,
    disabled: true,
    key: 'ellipsis',
    __ellipsis: true,
  };

  return [first, ellipsisCrumb, ...tail];
};

const renderIconAndLabel = (crumb: InternalCrumb): React.ReactNode => (
  <span style={{ display: 'inline-flex', gap: '0.25rem', alignItems: 'center' }}>
    {crumb.icon}
    <span>{crumb.label}</span>
  </span>
);

const createClickHandler = (crumb: InternalCrumb): React.MouseEventHandler<HTMLElement> => (event) => {
  if (!crumb.href) {
    event.preventDefault();
  }

  crumb.onClick?.(event);
};

const renderMuiCrumb = (
  crumb: InternalCrumb,
  options: { isLast: boolean; separatorProvided: boolean },
): React.ReactNode => {
  const { isLast, separatorProvided } = options;
  const content = renderIconAndLabel(crumb);

  if (isEllipsis(crumb)) {
    return (
      <Typography
        key={crumb.key}
        color="text.secondary"
        aria-hidden={separatorProvided}
        tabIndex={separatorProvided ? -1 : 0}
      >
        {crumb.label}
      </Typography>
    );
  }

  if (crumb.disabled || (isLast && !crumb.href)) {
    return (
      <Typography
        key={crumb.key}
        color={isLast ? 'text.primary' : 'text.secondary'}
        aria-current={isLast && !crumb.href ? 'page' : undefined}
        aria-disabled={crumb.disabled ? true : undefined}
        data-testid={crumb['data-testid']}
      >
        {content}
      </Typography>
    );
  }

  if (crumb.href) {
    return (
      <MuiLink
        key={crumb.key}
        component={LinkAdapter}
        href={crumb.href}
        underline="hover"
        color="inherit"
        onClick={crumb.onClick ? createClickHandler(crumb) : undefined}
        data-testid={crumb['data-testid']}
      >
        {content}
      </MuiLink>
    );
  }

  if (crumb.onClick) {
    return (
      <MuiLink
        key={crumb.key}
        component="button"
        type="button"
        underline="hover"
        color="inherit"
        onClick={createClickHandler(crumb)}
        data-testid={crumb['data-testid']}
        sx={{ background: 'none', border: 0, padding: 0, font: 'inherit' }}
      >
        {content}
      </MuiLink>
    );
  }

  return (
    <Typography key={crumb.key} color="text.secondary" data-testid={crumb['data-testid']}>
      {content}
    </Typography>
  );
};

const renderBootstrapCrumb = (
  crumb: InternalCrumb,
  options: { isLast: boolean; separatorProvided: boolean },
): React.ReactNode => {
  const { isLast, separatorProvided } = options;
  const content = renderIconAndLabel(crumb);

  if (isEllipsis(crumb)) {
    return (
      <Breadcrumb.Item
        key={crumb.key}
        active={false}
        linkAs="span"
        aria-hidden={separatorProvided}
        tabIndex={separatorProvided ? -1 : 0}
        data-testid={crumb['data-testid']}
      >
        {crumb.label}
      </Breadcrumb.Item>
    );
  }

  if (crumb.disabled) {
    return (
      <Breadcrumb.Item
        key={crumb.key}
        linkAs="span"
        aria-disabled={true}
        data-testid={crumb['data-testid']}
        linkProps={{ 'aria-disabled': true, tabIndex: -1 }}
      >
        {content}
      </Breadcrumb.Item>
    );
  }

  if (isLast && !crumb.href) {
    return (
      <Breadcrumb.Item
        key={crumb.key}
        active
        aria-current="page"
        data-testid={crumb['data-testid']}
      >
        {content}
      </Breadcrumb.Item>
    );
  }

  if (crumb.href) {
    const resolvedHref = toHrefString(crumb.href);
    return (
      <Breadcrumb.Item
        key={crumb.key}
        linkAs={LinkAdapter as React.ElementType}
        href={resolvedHref}
        linkProps={{
          onClick: crumb.onClick ? createClickHandler(crumb) : undefined,
          'data-testid': crumb['data-testid'],
        }}
      >
        {content}
      </Breadcrumb.Item>
    );
  }

  if (crumb.onClick) {
    return (
      <Breadcrumb.Item
        key={crumb.key}
        linkAs="button"
        linkProps={{
          type: 'button',
          onClick: createClickHandler(crumb),
          className: 'btn btn-link p-0 align-baseline',
          'data-testid': crumb['data-testid'],
        }}
      >
        {content}
      </Breadcrumb.Item>
    );
  }

  return (
    <Breadcrumb.Item key={crumb.key} linkAs="span" data-testid={crumb['data-testid']}>
      {content}
    </Breadcrumb.Item>
  );
};

const renderBootstrapList = (
  items: InternalCrumb[],
  separator: React.ReactNode | undefined,
): React.ReactNode[] => {
  const lastIndex = items.length - 1;

  return items.flatMap((crumb, index) => {
    const crumbNode = renderBootstrapCrumb(crumb, {
      isLast: index === lastIndex,
      separatorProvided: Boolean(separator),
    });

    if (!separator || index === lastIndex) {
      return [crumbNode];
    }

    return [
      crumbNode,
      <li
        key={`separator-${crumb.key}`}
        className="breadcrumb-separator"
        aria-hidden="true"
        style={{ display: 'inline-flex', alignItems: 'center' }}
      >
        {separator}
      </li>,
    ];
  });
};

/**
 * Breadcrumb reutilizável com suporte aos temas Material-UI e React-Bootstrap.
 */
export function AppBreadcrumb({
  items,
  variant = 'mui',
  separator,
  maxItems,
  className,
  style,
  id,
}: AppBreadcrumbProps): React.ReactElement {
  const normalizedItems = React.useMemo(() => withKeys(items), [items]);
  const visibleItems = React.useMemo(
    () => compactItems(normalizedItems, maxItems),
    [normalizedItems, maxItems],
  );
  const lastIndex = visibleItems.length - 1;

  const bootstrapChildren = React.useMemo(
    () => renderBootstrapList(visibleItems, separator),
    [visibleItems, separator],
  );

  return (
    <nav id={id} className={className} style={style} aria-label="breadcrumb" role="navigation">
      {variant === 'bootstrap' ? (
        <Breadcrumb>{bootstrapChildren}</Breadcrumb>
      ) : (
        <Breadcrumbs separator={separator} aria-label={undefined}>
          {visibleItems.map((crumb, index) =>
            renderMuiCrumb(crumb, {
              isLast: index === lastIndex,
              separatorProvided: Boolean(separator),
            }),
          )}
        </Breadcrumbs>
      )}
    </nav>
  );
}

/**
 * Exemplo:
 * <AppBreadcrumb
 *   variant="mui"
 *   items={[
 *     { label: 'Home', href: '/', icon: <HomeIcon fontSize="small" /> },
 *     { label: 'Torneios', href: '/torneios' },
 *     { label: 'Open de Manaus', href: '/torneios/123' },
 *     { label: 'Inscrições' } // ativo (último)
 *   ]}
 * />
 */









